import React from 'react';
import { useGetAddedTogetherQuery, useGetStorefrontQuery } from '../../Api/Api';
import CAN, { PRICING } from '../Auth/Permissions';
import { parseProductPageConfig } from '../Helpers/productPageConfig';
import { useLocalization } from '../Helpers/useLocalization';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import ProductScroller from '../FeaturedProducts/ProductScroller';

interface IFrequentlyAddedTogetherProps {
    productId?: string;
}

// "Frequently Added Together" row on the product page — basic product cards from the addedTogether
// BFF endpoint (GET /products/{id}/addedTogether), gated by the storefront's
// enableFrequentlyAddedTogether setting. Rendered as a horizontal side-scroller (ProductScroller),
// matching the featured / "Recommended For You" rows, and using the productPageConfig addedTogether*
// style keys for the section/heading/card so it reads as part of the product page.
const FrequentlyAddedTogether: React.FC<IFrequentlyAddedTogetherProps> = ({ productId }) => {
    const canSeePricing = CAN(PRICING);
    const L = useLocalization();
    const sfSettings = useGetStorefrontQuery();
    const enabled = sfSettings.data?.enableFrequentlyAddedTogether ?? false;
    // Skip the fetch entirely when the setting is off — no point calling a feature-flagged endpoint
    // the BFF will just return empty for anyway.
    const addedTogether = useGetAddedTogetherQuery({ productId: productId ?? '' }, { skip: !productId || !enabled });

    const config = React.useMemo(
        () => parseProductPageConfig(sfSettings.data?.productPageConfig),
        [sfSettings.data?.productPageConfig]
    );

    if (!enabled || addedTogether.isLoading || addedTogether.error) return null;
    const data = addedTogether.data;
    if (!data || data.length === 0) return null;

    // The endpoint may not gate pricing itself — defensively strip it here too, same as LinkedProducts.
    const products = canSeePricing ? data : data.map((p) => ({ ...p, price: undefined }));

    return (
        <div style={{ marginTop: '2em', ...elementStyleFrom(config.addedTogetherSection) }}>
            <h2 style={{ lineHeight: 1.3, marginBottom: '0.75em', ...elementStyleFrom(config.addedTogetherHeading) }}>{L('localization_added_together_heading')}</h2>
            <ProductScroller
                products={products}
                showFeaturedFlag={false}
                cardStyle={elementStyleFrom(config.addedTogetherCard)}
                scrollButtonStyle={elementStyleFrom(config.addedTogetherScrollButton)}
                scrollbarStyle={elementStyleFrom(config.addedTogetherScrollbar)}
                source="product"
            />
        </div>
    );
};

export default FrequentlyAddedTogether;
